import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Mail,
  Calendar,
  Link2,
  FileText,
  Briefcase,
  Zap,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { getUserById, disableUser, enableUser } from "@/services/admin-api";
import { ExtendTrialPanel } from "@/components/billing/ExtendTrialPanel";
import { resolveExtendTrialBase } from "@/lib/extend-trial";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_HIERARCHY, toSignupSourceBucket } from "@atsready/shared";
import { signupSourceBadge } from "@/lib/signup-source";

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: adminUser } = useAuth();
  const [now] = useState(() => new Date());

  const canModify = adminUser && ROLE_HIERARCHY[adminUser.role] >= ROLE_HIERARCHY["ADMIN"];

  const { data, isLoading } = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => getUserById(id!),
    enabled: !!id,
  });

  // These two toast and do not invalidate, and that is the difference from
  // `PlanManagement`: a failed disable/enable writes nothing, so there is no
  // divergence between screen and record to repair. It is the mutation inside
  // `ExtendTrialPanel` that they match — it left this page with the form.
  const disableMutation = useMutation({
    mutationFn: () => disableUser(id!, "Admin action"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      toast({ title: "User disabled" });
    },
    onError: (error) => {
      toast({
        title: "Failed to disable user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const enableMutation = useMutation({
    mutationFn: () => enableUser(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      toast({ title: "User enabled" });
    },
    onError: (error) => {
      toast({ title: "Failed to enable user", description: error.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const user = data?.data;
  if (!user) {
    return <div className="p-8 text-center">User not found</div>;
  }

  const signupBucket = toSignupSourceBucket(user.signupSource);
  const signupPill = signupSourceBadge(signupBucket);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{user.name || "No name"}</h2>
          <p className="text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* User Info */}
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{user.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Joined {format(new Date(user.createdAt), "MMMM d, yyyy")}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm text-muted-foreground">Signup source</span>
              {signupBucket === "unknown" ? (
                <>
                  <span aria-hidden="true" className="text-muted-foreground">
                    {"\u2014"}
                  </span>
                  <span className="sr-only">Not recorded</span>
                </>
              ) : (
                <>
                  <Badge variant={signupPill.variant} className={signupPill.className}>
                    {signupPill.label}
                  </Badge>
                  {user.signupCampaign && (
                    <>
                      <span aria-hidden="true" className="text-muted-foreground">
                        ·
                      </span>
                      <span title="Campaign" className="min-w-0 break-words font-mono text-sm">
                        <span className="sr-only">Campaign: </span>
                        {user.signupCampaign}
                      </span>
                    </>
                  )}
                  {user.signupReferrerHost && (
                    <>
                      <span aria-hidden="true" className="text-muted-foreground">
                        ·
                      </span>
                      <span
                        title="Referrer host"
                        className="min-w-0 break-words text-sm text-muted-foreground"
                      >
                        <span className="sr-only">Referrer host: </span>
                        {user.signupReferrerHost}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline">Role: {user.role}</Badge>
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span>{user.stats.resumeCount} resumes</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <span>{user.stats.jobCount} jobs</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription</CardTitle>
            <CardDescription>Current subscription details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {user.subscription ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tier</span>
                  <Badge variant={getTierVariant(user.subscription.tier)}>
                    {user.subscription.tier}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge variant={getStatusVariant(user.subscription.status)}>
                    {user.subscription.status}
                  </Badge>
                </div>
                {user.subscription.trialEndDate && (
                  <div className="flex items-center justify-between">
                    {/* The label follows the date rather than the status column:
                        a row the sweep has already moved off `TRIALING` still
                        carries the date, and "Trial Ends" in the past tense is
                        the one thing the operator must not read here. */}
                    <span className="text-sm text-muted-foreground">
                      {resolveExtendTrialBase(user.subscription.trialEndDate, now).expired
                        ? "Trial Ended"
                        : "Trial Ends"}
                    </span>
                    <span className="font-medium">
                      {format(new Date(user.subscription.trialEndDate), "MMM d, yyyy")}
                    </span>
                  </div>
                )}

                {/* `M13` — the form is not wrapped in `canModify`: the panel
                    states the role refusal in words rather than leaving a gap
                    where a control used to be, and it is its own gate. */}
                <div className="border-t border-border pt-4">
                  <ExtendTrialPanel
                    userId={id}
                    subscription={user.subscription}
                    role={adminUser ? adminUser.role : null}
                    isSelf={adminUser ? adminUser.id === id : false}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4">
                  {canModify && (
                    <>
                      {user.subscription.status === "CANCELED" ? (
                        <Button
                          size="sm"
                          onClick={() => enableMutation.mutate()}
                          disabled={enableMutation.isPending}
                        >
                          Enable Account
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => disableMutation.mutate()}
                          disabled={disableMutation.isPending}
                        >
                          Disable Account
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">No subscription found</p>
            )}
          </CardContent>
        </Card>

        {/* AI Usage Summary */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              AI Usage
            </CardTitle>
            <CardDescription>Lifetime token usage and cost</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total Calls</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {user.aiUsage?.totalCalls?.toLocaleString() ?? 0}
                </div>
                <div className="mt-1 flex gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    {user.aiUsage?.successCalls?.toLocaleString() ?? 0}
                  </span>
                  {(user.aiUsage?.errorCalls ?? 0) > 0 && (
                    <span className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-red-500" />
                      {user.aiUsage.errorCalls.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Input Tokens</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {user.aiUsage?.totalInputTokens?.toLocaleString() ?? 0}
                </div>
                {(user.aiUsage?.totalCachedTokens ?? 0) > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    ({user.aiUsage.totalCachedTokens.toLocaleString()} cached)
                  </div>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Output Tokens</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {user.aiUsage?.totalOutputTokens?.toLocaleString() ?? 0}
                </div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total Cost</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  ${(user.aiUsage?.totalCost ?? 0).toFixed(4)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent AI Executions */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Recent AI Executions</CardTitle>
            <CardDescription>Last 10 AI operations</CardDescription>
          </CardHeader>
          <CardContent>
            {!user.recentActivity || user.recentActivity.length === 0 ? (
              <p className="text-muted-foreground">No AI executions yet</p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prompt</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Latency</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.recentActivity.map((exec) => (
                      <TableRow key={exec.id}>
                        <TableCell className="font-mono text-xs">{exec.action}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {exec.model}
                          {exec.provider && exec.provider !== "openai" && (
                            <Badge variant="info" className="ml-1 text-[10px]">
                              {exec.provider}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">
                          {(exec.inputTokens + exec.outputTokens).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">
                          ${exec.costUsd.toFixed(4)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                          {exec.latencyMs}ms
                        </TableCell>
                        <TableCell>
                          <Badge variant={exec.status === "success" ? "success" : "destructive"}>
                            {exec.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(exec.createdAt), "MMM d, HH:mm")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function getTierVariant(tier: string): "default" | "info" | "secondary" {
  switch (tier) {
    case "PRO":
      return "info";
    case "FREE":
      return "secondary";
    default:
      return "default";
  }
}

function getStatusVariant(
  status: string,
): "success" | "info" | "warning" | "destructive" | "outline" {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "TRIALING":
      return "info";
    case "PAST_DUE":
      return "warning";
    case "CANCELED":
      return "destructive";
    default:
      return "outline";
  }
}
